// =========================================================================
// HD-040 · ConsentSignature — firma electrónica del consentimiento (Email OTP)
// Flujo: 1) Revisar → 2) Código OTP por email → 3) Firmado.
// Fiel al diseño aprobado docs/08_.../flujo-firma-consentimiento.html:
// banda con degradado navy→azul→verde, logo en tarjeta blanca, colores de marca.
// La lógica (mock/api) vive en el data-adapter; aquí solo UI + orquestación.
// =========================================================================
import { useState, useRef, useCallback, useEffect } from 'react';
import { Check } from 'lucide-react';
import { ICONOS_ESTADO } from '@/config/iconografia';
import {
  requestConsentOtp,
  verifyConsentSignature,
  CONSENT_DOCUMENT_VERSION,
  type ConsentDecisionMap,
  type ConsentType,
  type ConsentSignature as ConsentSignatureRecord,
} from '@/services';
import { LegalModal } from '@/components/LegalModal';
import type { LegalDoc } from '@/legal/legal-content';
import { REQUIRED_ITEMS, OPTIONAL_ITEMS, BRAND } from '@/components/consent-content';

type Step = 'review' | 'sign' | 'done';

interface ConsentSignatureProps {
  /** Se invoca al firmar con éxito (o al pulsar «Entrar»). */
  onDone: () => void;
  /** «Ahora no» → normalmente cerrar sesión. */
  onCancel: () => void;
  /** Email del usuario (para «código enviado a …»). */
  email?: string;
}

const brandFont = '-apple-system, "Hanken Grotesk", "Segoe UI", Arial, sans-serif';

export function ConsentSignature({ onDone, onCancel, email }: ConsentSignatureProps) {
  const [step, setStep] = useState<Step>('review');
  // Obligatorios marcados por defecto; opcionales desmarcados.
  const [checks, setChecks] = useState<Record<ConsentType, boolean>>(() => {
    const init = {} as Record<ConsentType, boolean>;
    REQUIRED_ITEMS.forEach(i => { init[i.type] = true; });
    OPTIONAL_ITEMS.forEach(i => { init[i.type] = false; });
    return init;
  });
  const [legalDoc, setLegalDoc] = useState<LegalDoc['id'] | null>(null);

  // Estado de firma
  const [requestId, setRequestId] = useState<string | null>(null);
  const [devCode, setDevCode] = useState<string | null>(null);
  const [otp, setOtp] = useState<string[]>(['', '', '', '', '', '']);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [signature, setSignature] = useState<ConsentSignatureRecord | null>(null);
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);

  const allRequired = REQUIRED_ITEMS.every(i => checks[i.type]);
  const emailMasked = email ? maskEmail(email) : 'tu email';

  const buildConsentMap = useCallback((): ConsentDecisionMap => {
    const map: ConsentDecisionMap = {};
    [...REQUIRED_ITEMS, ...OPTIONAL_ITEMS].forEach(i => {
      map[i.type] = checks[i.type] ? 'granted' : 'denied';
    });
    return map;
  }, [checks]);

  // ── Paso 1 → 2: pedir OTP ──
  const requestOtp = useCallback(async () => {
    setSending(true);
    setError(null);
    try {
      const res = await requestConsentOtp(CONSENT_DOCUMENT_VERSION);
      setRequestId(res.requestId);
      setDevCode(res.devCode ?? null);
      setStep('sign');
      setOtp(['', '', '', '', '', '']);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo enviar el código. Inténtalo de nuevo.');
    } finally {
      setSending(false);
    }
  }, []);

  // Foco en el primer input al entrar en el paso de firma
  useEffect(() => {
    if (step === 'sign') inputsRef.current[0]?.focus();
  }, [step]);

  const onOtpChange = (idx: number, val: string) => {
    const digit = val.replace(/\D/g, '').slice(-1);
    setOtp(prev => {
      const next = [...prev];
      next[idx] = digit;
      return next;
    });
    if (digit && idx < 5) inputsRef.current[idx + 1]?.focus();
  };

  const onOtpKeyDown = (idx: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[idx] && idx > 0) inputsRef.current[idx - 1]?.focus();
  };

  const onOtpPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (text.length) {
      e.preventDefault();
      const next = ['', '', '', '', '', ''];
      text.split('').forEach((c, i) => { next[i] = c; });
      setOtp(next);
      inputsRef.current[Math.min(text.length, 5)]?.focus();
    }
  };

  // ── Paso 2 → 3: verificar y firmar ──
  const doSign = useCallback(async () => {
    if (!requestId) return;
    const code = otp.join('');
    if (code.length !== 6) { setError('Introduce los 6 dígitos del código.'); return; }
    setVerifying(true);
    setError(null);
    try {
      const sig = await verifyConsentSignature({
        requestId,
        code,
        consents: buildConsentMap(),
        documentVersion: CONSENT_DOCUMENT_VERSION,
      });
      setSignature(sig);
      setStep('done');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo verificar el código.');
    } finally {
      setVerifying(false);
    }
  }, [requestId, otp, buildConsentMap]);

  const resend = useCallback(async () => {
    setOtp(['', '', '', '', '', '']);
    await requestOtp();
  }, [requestOtp]);

  return (
    <div style={{ height: '100%', overflowY: 'auto', background: '#F2F5FA', fontFamily: brandFont, color: BRAND.ink900 }}>
      {/* Banda de marca con logo */}
      <div style={{ background: BRAND.grad, padding: '24px 18px 26px', textAlign: 'center', borderRadius: '0 0 22px 22px' }}>
        <div style={{ display: 'inline-block', background: '#fff', borderRadius: 18, padding: '12px 22px', boxShadow: '0 10px 28px rgba(0,27,94,.28)' }}>
          <img
            src={`${import.meta.env.BASE_URL}helpydoc-logo-alpha.png`}
            alt="HelpyDoc"
            style={{ width: 148, height: 'auto', display: 'block' }}
          />
        </div>
      </div>

      <div style={{ maxWidth: 560, margin: '0 auto', padding: '0 18px 40px' }}>
        {/* Indicador de pasos */}
        <div style={{ display: 'flex', gap: 6, margin: '14px 0 8px', flexWrap: 'wrap' }}>
          {(['review', 'sign', 'done'] as Step[]).map((s, i) => {
            const on = step === s;
            const labels = ['1 · Revisar', '2 · Firmar', '3 · Firmado'];
            return (
              <span key={s} style={{
                fontSize: 12, padding: '6px 11px', borderRadius: 999,
                background: on ? BRAND.navy : '#fff', color: on ? '#fff' : BRAND.ink500,
                border: `1px solid ${on ? BRAND.navy : BRAND.ink100}`,
              }}>{labels[i]}</span>
            );
          })}
        </div>

        {step === 'review' && (
          <ReviewStep
            checks={checks}
            setChecks={setChecks}
            allRequired={allRequired}
            sending={sending}
            error={error}
            onOpenLegal={setLegalDoc}
            onSign={requestOtp}
            onCancel={onCancel}
          />
        )}

        {step === 'sign' && (
          <SignStep
            emailMasked={emailMasked}
            otp={otp}
            inputsRef={inputsRef}
            onOtpChange={onOtpChange}
            onOtpKeyDown={onOtpKeyDown}
            onOtpPaste={onOtpPaste}
            onSign={doSign}
            onResend={resend}
            verifying={verifying}
            sending={sending}
            error={error}
            devCode={devCode}
          />
        )}

        {step === 'done' && signature && (
          <DoneStep signature={signature} emailMasked={emailMasked} onEnter={onDone} />
        )}
      </div>

      <LegalModal docId={legalDoc} onClose={() => setLegalDoc(null)} />
    </div>
  );
}

// ── Paso 1 · Revisar ────────────────────────────────────────────────────────
function ReviewStep({
  checks, setChecks, allRequired, sending, error, onOpenLegal, onSign, onCancel,
}: {
  checks: Record<ConsentType, boolean>;
  setChecks: React.Dispatch<React.SetStateAction<Record<ConsentType, boolean>>>;
  allRequired: boolean;
  sending: boolean;
  error: string | null;
  onOpenLegal: (id: LegalDoc['id']) => void;
  onSign: () => void;
  onCancel: () => void;
}) {
  const toggle = (t: ConsentType) => setChecks(prev => ({ ...prev, [t]: !prev[t] }));
  const legalFor: Partial<Record<ConsentType, LegalDoc['id']>> = {
    terms_of_service: 'terms',
    privacy_policy: 'privacy',
    health_data_processing: 'disclaimer',
  };
  return (
    <>
      <h1 style={h1Style}>Consentimiento de inscripción</h1>
      <p style={subStyle}>
        Se firma <b>una sola vez</b> al inscribirte. Después no se te vuelve a pedir; podrás consultarlo en Ajustes.
      </p>

      {REQUIRED_ITEMS.map(it => (
        <Card key={it.type} kind="req">
          <input type="checkbox" checked={checks[it.type]} onChange={() => toggle(it.type)} style={checkboxStyle} aria-label={it.title} />
          <div style={{ flex: 1 }}>
            <div style={titleRow}>
              {it.title}
              <span style={tagOb}>Obligatorio</span>
            </div>
            <div style={descStyle}>
              {it.desc}{' '}
              {legalFor[it.type] && (
                <button type="button" onClick={() => onOpenLegal(legalFor[it.type]!)} style={linkBtn}>Ver documento</button>
              )}
            </div>
          </div>
        </Card>
      ))}

      {OPTIONAL_ITEMS.map(it => (
        <Card key={it.type} kind="opt">
          <input type="checkbox" checked={checks[it.type]} onChange={() => toggle(it.type)} style={checkboxStyle} aria-label={it.title} />
          <div style={{ flex: 1 }}>
            <div style={titleRow}>
              {it.title}
              <span style={tagOp}>Opcional</span>
            </div>
            <div style={descStyle}>{it.desc}</div>
            {it.detail && <div style={detailStyle}>{it.detail}</div>}
            {it.chips && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 7 }}>
                {it.chips.map(c => <span key={c} style={chipStyle}>{c}</span>)}
              </div>
            )}
          </div>
        </Card>
      ))}

      {error && (
        <div style={{ ...errorBox, display: 'flex', alignItems: 'center', gap: 6 }}>
          <ICONOS_ESTADO.advertencia size={14} style={{ flexShrink: 0 }} aria-hidden="true" />
          <span>{error}</span>
        </div>
      )}

      <button onClick={onSign} disabled={!allRequired || sending} style={btnPrimary(!allRequired || sending)}>
        {sending ? 'Enviando código…' : 'Firmar y aceptar →'}
      </button>
      {!allRequired && <div style={hintStyle}>Marca los obligatorios para firmar.</div>}
      <button onClick={onCancel} disabled={sending} style={btnGhost}>Ahora no (cerrar sesión)</button>
    </>
  );
}

// ── Paso 2 · Firmar (OTP) ───────────────────────────────────────────────────
function SignStep({
  emailMasked, otp, inputsRef, onOtpChange, onOtpKeyDown, onOtpPaste, onSign, onResend, verifying, sending, error, devCode,
}: {
  emailMasked: string;
  otp: string[];
  inputsRef: React.MutableRefObject<Array<HTMLInputElement | null>>;
  onOtpChange: (i: number, v: string) => void;
  onOtpKeyDown: (i: number, e: React.KeyboardEvent<HTMLInputElement>) => void;
  onOtpPaste: (e: React.ClipboardEvent<HTMLInputElement>) => void;
  onSign: () => void;
  onResend: () => void;
  verifying: boolean;
  sending: boolean;
  error: string | null;
  devCode: string | null;
}) {
  return (
    <>
      <h1 style={h1Style}>Firma electrónica</h1>
      <p style={subStyle}>
        Introduce el <b>código de 6 dígitos</b> que hemos enviado a <b>{emailMasked}</b>. La firma queda registrada con fecha y hora.
      </p>

      <div style={{ display: 'flex', gap: 8, justifyContent: 'center', margin: '16px 0' }}>
        {otp.map((d, i) => (
          <input
            key={i}
            ref={el => { inputsRef.current[i] = el; }}
            value={d}
            onChange={e => onOtpChange(i, e.target.value)}
            onKeyDown={e => onOtpKeyDown(i, e)}
            onPaste={onOtpPaste}
            inputMode="numeric"
            maxLength={1}
            aria-label={`Dígito ${i + 1}`}
            style={otpInput}
          />
        ))}
      </div>

      {devCode && (
        <div style={devHint}>Modo demo · código: <b style={{ letterSpacing: 2 }}>{devCode}</b></div>
      )}

      {error && (
        <div style={{ ...errorBox, display: 'flex', alignItems: 'center', gap: 6 }}>
          <ICONOS_ESTADO.advertencia size={14} style={{ flexShrink: 0 }} aria-hidden="true" />
          <span>{error}</span>
        </div>
      )}

      <button onClick={onSign} disabled={verifying} style={btnPrimary(verifying)}>
        {verifying ? 'Firmando…' : 'Firmar aceptación'}
      </button>
      <button onClick={onResend} disabled={sending || verifying} style={btnGhost}>
        {sending ? 'Reenviando…' : 'Reenviar código'}
      </button>
      <div style={hintStyle}>¿No te llega? Revisa spam o reenvía. Caduca en 10 min.</div>
    </>
  );
}

// ── Paso 3 · Firmado ────────────────────────────────────────────────────────
function DoneStep({ signature, emailMasked, onEnter }: {
  signature: ConsentSignatureRecord;
  emailMasked: string;
  onEnter: () => void;
}) {
  return (
    <>
      <div style={okCircle}><Check size={32} strokeWidth={2.5} aria-hidden="true" /></div>
      <h1 style={{ ...h1Style, textAlign: 'center' }}>Consentimiento firmado</h1>
      <p style={{ ...subStyle, textAlign: 'center' }}>
        Firmado el <b>{formatDate(signature.signedAt)}</b>. Te hemos enviado una <b>copia firmada (PDF)</b>.
      </p>

      <div style={infoCard}>
        <Row k="Documento" v={signature.documentVersion} />
        <Row k="Método" v={methodLabel(signature.method)} />
        <Row k="Copia enviada a" v={signature.emailChannel ? maskEmail(signature.emailChannel) : emailMasked} />
        {signature.documentHash && <Row k="Integridad" v={signature.documentHash} last mono />}
      </div>

      <button onClick={onEnter} style={btnPrimary(false)}>Entrar a HelpyDoc</button>
      <div style={hintStyle}>Consulta y descarga en Ajustes › Privacidad.</div>
    </>
  );
}

// ── Piezas visuales ──────────────────────────────────────────────────────────
function Card({ kind, children }: { kind: 'req' | 'opt'; children: React.ReactNode }) {
  return (
    <label style={{
      background: '#fff', border: `1px solid ${BRAND.ink100}`,
      borderLeft: `3px solid ${kind === 'req' ? BRAND.blue : BRAND.green}`,
      borderRadius: 14, padding: '13px 15px', margin: '9px 0',
      display: 'flex', gap: 11, alignItems: 'flex-start', boxShadow: '0 2px 12px rgba(0,27,94,.08)', cursor: 'pointer',
    }}>{children}</label>
  );
}

function Row({ k, v, last, mono }: { k: string; v: string; last?: boolean; mono?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '10px 0', borderBottom: last ? 'none' : `1px solid ${BRAND.ink100}` }}>
      <span style={{ color: BRAND.ink500, fontSize: 13 }}>{k}</span>
      <span style={{ color: BRAND.ink900, fontSize: mono ? 11 : 13, fontWeight: 600, textAlign: 'right', wordBreak: 'break-all' }}>{v}</span>
    </div>
  );
}

// ── Utilidades ───────────────────────────────────────────────────────────────
export function maskEmail(email: string): string {
  const [user, domain] = email.split('@');
  if (!domain) return email;
  const head = user.slice(0, Math.min(3, user.length));
  return `${head}…@${domain}`;
}

function methodLabel(method: string): string {
  return method === 'email_otp' ? 'Código email (OTP)' : method;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('es', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

// ── Estilos ──────────────────────────────────────────────────────────────────
const h1Style: React.CSSProperties = { fontSize: 20, margin: '16px 0 4px', color: BRAND.navy };
const subStyle: React.CSSProperties = { color: BRAND.ink500, fontSize: 13, margin: '0 0 12px', lineHeight: 1.5 };
const titleRow: React.CSSProperties = { fontWeight: 700, fontSize: 14.5, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', color: BRAND.ink900 };
const descStyle: React.CSSProperties = { fontSize: 12.5, color: BRAND.ink700, marginTop: 3, lineHeight: 1.45 };
const detailStyle: React.CSSProperties = { background: '#F4F6FB', borderRadius: 10, padding: '9px 11px', marginTop: 8, fontSize: 12, color: BRAND.ink700 };
const checkboxStyle: React.CSSProperties = { marginTop: 3, width: 19, height: 19, accentColor: BRAND.green, flexShrink: 0 };
const chipStyle: React.CSSProperties = { fontSize: 10.5, background: '#fff', border: `1px solid ${BRAND.ink100}`, borderRadius: 8, padding: '3px 7px', color: BRAND.ink500 };
const tagOb: React.CSSProperties = { fontSize: 10, padding: '2px 7px', borderRadius: 999, background: BRAND.blue, color: '#fff' };
const tagOp: React.CSSProperties = { fontSize: 10, padding: '2px 7px', borderRadius: 999, background: '#E6F9F2', color: BRAND.greenDeep };
const hintStyle: React.CSSProperties = { color: BRAND.ink500, fontSize: 12, textAlign: 'center', marginTop: 9 };
const devHint: React.CSSProperties = { ...hintStyle, background: '#FFF7E6', border: '1px solid #FFE0A3', color: '#8A5A00', borderRadius: 10, padding: '7px 10px' };
const linkBtn: React.CSSProperties = { background: 'none', border: 'none', padding: 0, color: BRAND.blue, textDecoration: 'underline', cursor: 'pointer', fontSize: 12.5, fontWeight: 600 };
const errorBox: React.CSSProperties = { marginTop: 12, color: '#8A1C0A', background: '#FCE4DC', border: '1px solid #F5B5A5', borderRadius: 12, padding: '10px 12px', fontSize: 13 };
const otpInput: React.CSSProperties = { width: 44, height: 54, textAlign: 'center', fontSize: 22, borderRadius: 10, border: `1px solid ${BRAND.ink300}`, background: '#fff', color: BRAND.ink900, outline: 'none' };
const okCircle: React.CSSProperties = { width: 64, height: 64, borderRadius: '50%', background: BRAND.green, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32, color: '#fff', margin: '18px auto 4px', boxShadow: BRAND.shadowBtn };
const infoCard: React.CSSProperties = { background: '#fff', border: `1px solid ${BRAND.ink100}`, borderRadius: 14, padding: '4px 15px', margin: '12px 0', boxShadow: '0 2px 12px rgba(0,27,94,.08)' };

function btnPrimary(disabled: boolean): React.CSSProperties {
  return {
    width: '100%', border: 0, borderRadius: 12, padding: 14, fontSize: 15, fontWeight: 700,
    cursor: disabled ? 'not-allowed' : 'pointer', marginTop: 10,
    background: disabled ? '#B7E7D5' : BRAND.green, color: '#fff',
    boxShadow: disabled ? 'none' : BRAND.shadowBtn,
  };
}
const btnGhost: React.CSSProperties = {
  width: '100%', borderRadius: 12, padding: 14, fontSize: 15, fontWeight: 700, cursor: 'pointer', marginTop: 8,
  background: '#fff', color: BRAND.blue, border: `1px solid ${BRAND.ink100}`,
};
